
import React, {
  useState,
  useEffect,
} from "react"

import PulseLoader from "react-spinners/PulseLoader"

import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"

import useTitle from "@/hooks/useTitle"

import {
  useGetMyOrgIntentionResponsesQuery,
  useGetMyPeerIntentionResponsesQuery,
  useUpdateMyResponseSharingMutation,
} from "@/features/orgIntentionBoards/orgIntentionResponsesApiSlice"


/*
|--------------------------------------------------------------------------
| Learn From Others - 28 Sep 2026
|--------------------------------------------------------------------------
|
| Author - Atul Pandey
|
| Personal peer learning experience.
|
| - My response appears first.
| - Peer answers follow the same question.
| - Named / Anonymous / Private sharing preference.
| - Historical sharing defaults to Private.
| - Only submitted, authorized peer responses are displayed.
| - Updated - 28 Sep 2026 - Refined view for Fair Sharing
|
*/


/*
|--------------------------------------------------------------------------
| Response Display
|--------------------------------------------------------------------------
*/

const ResponseContent = ({
  response,
  questionType,
}) => {

  if (!response?.isAnswered && !response?.responseText &&
      !(response?.selectedOptions || []).length) {

    return (
      <p className="text-xs italic text-gray-400">
        No submitted response.
      </p>
    )

  }

  if (
    [
      "reflection",
      "short_text",
      "long_text",
    ].includes(questionType)
  ) {

    return (

      <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
        {response?.responseText || "No text response."}
      </p>

    )

  }


  if (
    [
      "single_select",
      "multi_select",
    ].includes(questionType)
  ) {

    const details =
      response?.selectedOptionDetails || []

    const options =
      details.length
        ? details.map(
            item =>
              item.optionLabel ||
              item.optionValue
          )
        : response?.selectedOptions || []

    return (

      <div className="flex flex-wrap gap-2">

        {options.map(
          (option, index) => (

            <span
              key={`${index}-${option}`}
              className="bg-orange-50 border border-orange-100 rounded px-3 py-2 text-[11px] text-gray-700"
            >
              {option}
            </span>

          )
        )}

      </div>

    )

  }

  return (

    <p className="text-xs text-gray-700 whitespace-pre-wrap">
      {response?.responseText || ""}
    </p>

  )

}


/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/

const EtUserLearnFromOthers = () => {

  useTitle("Learn From Others")


  /*
  |--------------------------------------------------------------------------
  | My Linked Intention Boards
  |--------------------------------------------------------------------------
  */

  const {
    data: myIntentionData,
    isLoading: isLoadingIntention,
  } = useGetMyOrgIntentionResponsesQuery()


  const eligibleJourneys =
    (myIntentionData?.journeys || [])
      .filter(
        journey =>
          journey.hasSubmittedResponse
      )


  const [
    selectedBoardId,
    setSelectedBoardId,
  ] = useState("")


  const [
    selectedQuestionIndex,
    setSelectedQuestionIndex,
  ] = useState(0)


  const [
    selectedVisibility,
    setSelectedVisibility,
  ] = useState("private")


  useEffect(() => {

    if (
      selectedBoardId &&
      eligibleJourneys.some(
        journey =>
          String(journey.intentionBoard?.intentionBoardId) ===
          selectedBoardId
      )
    ) {
      return
    }

    setSelectedBoardId(
      String(
        eligibleJourneys[0]
          ?.intentionBoard
          ?.intentionBoardId || ""
      )
    )

  }, [
    myIntentionData,
    selectedBoardId,
  ])


  /*
  |--------------------------------------------------------------------------
  | Peer Responses
  |--------------------------------------------------------------------------
  */

  const {
    data: peerData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetMyPeerIntentionResponsesQuery(
    {
      intentionBoardId:
        selectedBoardId,
    },
    {
      skip:
        !selectedBoardId,
    }
  )


  const [
    updateMyResponseSharing,
    {
      isLoading: isUpdatingSharing,
      isError: isSharingError,
      error: sharingError,
    },
  ] = useUpdateMyResponseSharingMutation()


  useEffect(() => {

    if (
      peerData?.mySharingPreference
    ) {

      setSelectedVisibility(
        peerData.mySharingPreference
      )

    }

  }, [
    peerData?.mySharingPreference,
    selectedBoardId,
  ])


  const questions =
    peerData?.questions || []


  const currentQuestion =
    questions[selectedQuestionIndex]


  /*
  |--------------------------------------------------------------------------
  | Board Change
  |--------------------------------------------------------------------------
  */

  const handleBoardChange = e => {

    setSelectedBoardId(
      e.target.value
    )

    setSelectedQuestionIndex(0)

    setSelectedVisibility("private")

  }


  /*
  |--------------------------------------------------------------------------
  | Update Sharing
  |--------------------------------------------------------------------------
  */

  const handleUpdateSharing =
    async () => {

      if (!selectedBoardId) {
        return
      }

      try {

        await updateMyResponseSharing({

          intentionBoardId:
            selectedBoardId,

          visibility:
            selectedVisibility,

        }).unwrap()

      } catch (error) {

        console.error(
          "Response sharing update failed:",
          error
        )

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Loading Linked Journeys
  |--------------------------------------------------------------------------
  */

  if (isLoadingIntention) {

    return (

      <div className="bg-white rounded-lg p-8 text-center">

        <PulseLoader color="#ea580c" size={5} />

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | No Submitted Journey
  |--------------------------------------------------------------------------
  */

  if (
    eligibleJourneys.length === 0
  ) {

    return (

      <div className="bg-white rounded-lg p-6 font-poppins">

        <p className="text-lg font-bold text-gray-800">
          Learn From Others
        </p>

        <p className="text-xs text-gray-500 mt-3">
          Peer learning becomes available after your Intention
          has been submitted and connected to your Trinity account.
        </p>

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="font-poppins space-y-4">

      {/* Header */}

      <div className="bg-white rounded-lg p-5">

        <div className="flex flex-wrap items-start justify-between gap-3">

          <div>

            <p className="text-xl font-bold text-gray-900">
              Learn From Others
            </p>

            <p className="text-xs italic text-gray-500 mt-1">
              Explore how fellow participants approached the same questions.
            </p>

          </div>

          <button
            type="button"
            onClick={() => {
              if (!isLoading && !isError) {
                refetch()
              }
            }}
            disabled={isFetching || !selectedBoardId}
            className="inline-flex items-center gap-2 rounded bg-orange-50 text-orange-700 px-3 py-2 text-[10px] font-bold disabled:opacity-40"
          >

            <ArrowPathIcon className="h-4 w-4" />

            Refresh

          </button>

        </div>


        {/* Board Selector */}

        {eligibleJourneys.length > 1 && (

          <div className="mt-4">

            <label className="block text-[10px] font-bold text-gray-600 mb-2">
              Intention Board
            </label>

            <select
              value={selectedBoardId}
              onChange={handleBoardChange}
              className="rounded border border-gray-200 bg-gray-50 p-2 text-xs w-full sm:max-w-sm"
            >

              {eligibleJourneys.map(
                journey => (

                  <option
                    key={journey.intentionBoard.intentionBoardId}
                    value={journey.intentionBoard.intentionBoardId}
                  >

                    {journey.intentionBoard.boardName}

                  </option>

                )
              )}

            </select>

          </div>

        )}

      </div>


      {/* Loading / Error */}

      {isLoading && (

        <div className="bg-white rounded p-6 text-center">

          <PulseLoader color="#ea580c" size={5} />

        </div>

      )}


      {isError && (

        <div className="bg-white rounded p-5">

          <p className="text-xs font-bold text-orange-700">
            Unable to retrieve peer responses.
          </p>

          <p className="text-xs text-gray-500 mt-2">
            {error?.data?.message ||
              "Please try refreshing this view."}
          </p>

        </div>

      )}


      {!isLoading &&
        !isError &&
        peerData && (

          <>

            {/* Sharing Preference */}

            <div className="bg-white border border-orange-100 rounded-lg p-4">

              <div className="flex items-start gap-2">

                <ShieldCheckIcon className="w-5 h-5 text-orange-600 shrink-0" />

                <div>

                  <p className="text-xs font-bold text-gray-800">
                    My Response Sharing
                  </p>

                  <p className="text-[10px] text-gray-500 mt-1">
                    Choose how your submitted responses appear to fellow
                    participants. Historical responses remain private until
                    you explicitly choose to share them.
                  </p>

                </div>

              </div>


              <div className="flex flex-wrap items-center gap-2 mt-4">

                <select
                  value={selectedVisibility}
                  onChange={e =>
                    setSelectedVisibility(e.target.value)
                  }
                  className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
                >

                  <option value="private">
                    Private – Do Not Share
                  </option>

                  <option value="anonymous">
                    Share Anonymously
                  </option>

                  <option value="named">
                    Share With My Name
                  </option>

                </select>


                <button
                  type="button"
                  onClick={handleUpdateSharing}
                  disabled={
                    isUpdatingSharing ||
                    selectedVisibility === peerData.mySharingPreference
                  }
                  className="rounded bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold px-4 py-2 disabled:opacity-40"
                >

                  {isUpdatingSharing
                    ? "Saving..."
                    : "Save Preference"}

                </button>

              </div>

              
            {/*
            |--------------------------------------------------------------------------
            | Fair Sharing Guidance - 28 Sep 2026
            |--------------------------------------------------------------------------
            */}

            {peerData.sharingRequired && (

            <div
                className="
                bg-orange-50
                border
                border-orange-100
                rounded-lg
                p-4
                mt-4
                "
            >

                <p className="text-xs font-bold text-orange-700">
                Sharing Opens Learning
                </p>

                <p className="text-[11px] text-gray-600 leading-relaxed mt-2">

                Learning from fellow participants begins with sharing
                your own perspectives.

                To explore their responses, choose to share yours
                either anonymously or with your name.

                Your responses will remain private until you choose
                to share them.

                </p>

            </div>

            )}


              {isSharingError && (

                <p className="text-[10px] text-red-700 mt-2">
                  {sharingError?.data?.message ||
                    "Unable to save sharing preference."}
                </p>

              )}

              <p className="text-[10px] text-gray-500 mt-3">
                Your current preference:{" "}
                <span className="font-bold text-gray-700">
                  {peerData.mySharingPreference === "named"
                    ? "Share With My Name"
                    : peerData.mySharingPreference === "anonymous"
                      ? "Share Anonymously"
                      : "Private"}
                </span>
              </p>

            </div>


            {/* Question Navigation */}

            <div className="bg-white rounded-lg p-4">

              <div className="flex flex-wrap justify-between items-center gap-3">

                <p className="text-xs font-bold text-gray-800">
                  Questions and Perspectives
                </p>

                <p className="text-[10px] text-gray-500">
                  {questions.length
                    ? `Question ${selectedQuestionIndex + 1} of ${questions.length}`
                    : "No available questions"}
                </p>

              </div>


              <div className="flex flex-wrap gap-2 mt-3">

                {questions.map(
                  (question, index) => (

                    <button
                      key={question.intentionQuestionId}
                      type="button"
                      onClick={() =>
                        setSelectedQuestionIndex(index)
                      }
                      className={`w-8 h-8 rounded-full text-[10px] font-bold ${
                        selectedQuestionIndex === index
                          ? "bg-orange-600 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {question.questionNumber || index + 1}
                    </button>

                  )
                )}

              </div>

            </div>


            {/* Current Question */}

            {currentQuestion && (

              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5">

                <p className="text-[10px] font-bold uppercase text-orange-700">
                  Question {currentQuestion.questionNumber}
                </p>

                <p className="text-sm font-bold text-gray-800 mt-2 leading-relaxed">
                  {currentQuestion.questionText}
                </p>

                {currentQuestion.questionDescription && (

                  <p className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">
                    {currentQuestion.questionDescription}
                  </p>

                )}


                {/* My Response */}

                <div className="bg-orange-50 rounded-lg p-4 mt-4">

                  <p className="text-[10px] font-bold text-orange-700 mb-3">
                    My Response
                  </p>

                  <ResponseContent
                    questionType={currentQuestion.questionType}
                    response={currentQuestion.myResponse}
                  />

                </div>


                {/* Peer Responses */}

                <div className="mt-5">

                  <div className="flex flex-wrap justify-between items-center gap-2">

                    <p className="text-xs font-bold text-gray-800">
                      Fellow Participants
                    </p>

                    <span className="rounded-full bg-gray-100 px-3 py-1 text-[10px] font-bold text-gray-600">
                      {currentQuestion.peerResponses?.length || 0} shared
                    </span>

                  </div>


                  {(currentQuestion.peerResponses || []).length === 0 ? (

                    <div className="bg-gray-50 rounded p-4 mt-3">

                      <p className="text-xs text-gray-500">
                        No fellow participant has shared a response
                        to this question yet.
                      </p>

                    </div>

                  ) : (

                    <div className="space-y-3 mt-3">

                      {currentQuestion.peerResponses.map(
                        (peer, index) => (

                          <div
                            key={`${selectedQuestionIndex}-${index}`}
                            className="border border-gray-100 rounded-lg p-4"
                          >

                            <div className="flex items-center gap-2 mb-3">

                              <UserCircleIcon className="w-5 h-5 text-orange-500" />

                              <p className="text-xs font-bold text-gray-800">
                                {peer.displayName}
                              </p>

                              {peer.visibility === "anonymous" && (

                                <span className="text-[9px] bg-gray-100 rounded-full px-2 py-1 text-gray-500">
                                  Anonymous
                                </span>

                              )}

                            </div>

                            <ResponseContent
                              response={peer}
                              questionType={currentQuestion.questionType}
                            />

                          </div>

                        )
                      )}

                    </div>

                  )}

                </div>


                {/* Previous / Next */}

                <div className="flex justify-between items-center gap-2 border-t border-gray-100 pt-4 mt-5">

                  <button
                    type="button"
                    disabled={selectedQuestionIndex === 0}
                    onClick={() =>
                      setSelectedQuestionIndex(
                        previous => previous - 1
                      )
                    }
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-600 disabled:opacity-30"
                  >

                    <ChevronLeftIcon className="w-4 h-4" />

                    Previous

                  </button>


                  <button
                    type="button"
                    disabled={
                      selectedQuestionIndex >=
                      questions.length - 1
                    }
                    onClick={() =>
                      setSelectedQuestionIndex(
                        previous => previous + 1
                      )
                    }
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-700 disabled:opacity-30"
                  >

                    Next

                    <ChevronRightIcon className="w-4 h-4" />

                  </button>

                </div>

              </div>

            )}

          </>

        )}

    </div>

  )

}


export default EtUserLearnFromOthers